import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hammer, CheckCircle, WarningCircle, CircleNotch, ArrowRight, Copy, Eye } from '@phosphor-icons/react';

const ForgeNewDeployment = () => {
  const [accounts, setAccounts] = useState([]);
  const [servers, setServers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    account_id: '', server_id: '', count: 1, source: 'git-inline',
    template_id: '', git_provider: 'github', repository: '', branch: 'main',
    create_database: false, project_type: 'php', directory: '/public',
  });
  const [deployments, setDeployments] = useState([]);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    axios.get('/api/forge/accounts', { withCredentials: true }).then(r => {
      setAccounts(r.data);
      if (r.data.length) setForm(f => ({ ...f, account_id: r.data[0].id }));
    }).catch(() => {});
    axios.get('/api/forge/templates', { withCredentials: true }).then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.account_id) { setServers([]); return; }
    axios.get(`/api/forge/accounts/${form.account_id}/servers`, { withCredentials: true })
      .then(r => { setServers(r.data); if (r.data.length) setForm(f => ({ ...f, server_id: r.data[0].id })); })
      .catch(() => setServers([]));
  }, [form.account_id]);

  const handleDeploy = async () => {
    if (!form.account_id) return toast.error('Select a Forge account');
    if (!form.server_id) return toast.error('Select a server (provision one under Forge Accounts if you have none)');

    const payload = {
      account_id: form.account_id,
      server_id: parseInt(form.server_id),
      count: form.count,
      create_database: form.create_database,
      project_type: form.project_type,
      directory: form.directory,
    };
    if (form.source === 'template') {
      if (!form.template_id) return toast.error('Choose a template');
      payload.template_id = form.template_id;
      payload.mode = 'template'; // backend resolves from template type
    } else if (form.source === 'git-inline') {
      if (!form.repository) return toast.error('Enter a git repository');
      payload.mode = 'git';
      payload.repository = form.repository;
      payload.git_provider = form.git_provider;
      payload.branch = form.branch;
    } else {
      payload.mode = 'blank';
    }

    setDeploying(true);
    try {
      const res = await axios.post('/api/forge/deployments', payload, { withCredentials: true });
      const initial = res.data.map(d => ({ ...d, url: null, error: null }));
      setDeployments(initial);
      initial.forEach((dep, index) => {
        const poll = async () => {
          try {
            const pr = await axios.get(`/api/forge/deployments/${dep.id}`, { withCredentials: true });
            setDeployments(prev => {
              const arr = [...prev];
              if (pr.data.status === 'live') arr[index] = { ...arr[index], status: 'live', url: pr.data.public_url };
              else if (pr.data.status === 'failed') arr[index] = { ...arr[index], status: 'failed', error: pr.data.error_msg, url: pr.data.public_url };
              else arr[index] = { ...arr[index], status: pr.data.status, url: pr.data.public_url };
              return arr;
            });
            if (pr.data.status !== 'live' && pr.data.status !== 'failed') setTimeout(poll, 6000);
          } catch (e) { setTimeout(poll, 8000); }
        };
        poll();
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deployment failed');
    }
    setDeploying(false);
  };

  const statusDisplay = (s) => {
    switch (s) {
      case 'live': return <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> Live</span>;
      case 'failed': return <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> Failed</span>;
      case 'creating': return <span className="text-yellow-400 text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Creating site…</span>;
      case 'installing': return <span className="text-[#FB503B] text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Installing…</span>;
      default: return <span className="text-slate-400 text-xs">{s}</span>;
    }
  };

  const copyAll = () => {
    const urls = deployments.filter(d => d.status === 'live' && d.url).map(d => d.url).join('\n');
    if (urls) { navigator.clipboard.writeText(urls); toast.success('Live URLs copied'); }
    else toast.error('No live URLs yet');
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black text-[#FB503B] flex items-center gap-3"><Hammer size={32} weight="fill" /> ONFORGE DEPLOYMENT</h1>
        <p className="text-slate-400 font-mono">Spin up N Laravel sites with free on-forge.com URLs.</p>
      </div>

      <div className="bg-card border border-white/10 p-8 max-w-3xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Forge Account</label>
            <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="forge-deploy-account" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#FB503B] appearance-none rounded-none">
              <option value="">-- account --</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Server</label>
            <select value={form.server_id} onChange={(e) => setForm({ ...form, server_id: e.target.value })} data-testid="forge-deploy-server" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#FB503B] appearance-none rounded-none">
              <option value="">-- server --</option>
              {servers.map(s => <option key={s.id} value={s.id} disabled={!s.is_ready}>{s.name} {s.is_ready ? '' : '(not ready)'}</option>)}
            </select>
            {servers.length === 0 && form.account_id && <p className="text-[10px] text-yellow-500 mt-1">No servers. Provision one under Forge Accounts.</p>}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Source</label>
          <div className="flex gap-2 flex-wrap">
            {[['git-inline', 'Git URL'], ['template', 'Template'], ['blank', 'Blank Site']].map(([v, l]) => (
              <button key={v} onClick={() => setForm({ ...form, source: v })} data-testid={`forge-source-${v}`}
                className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${form.source === v ? 'bg-[#FB503B] text-white' : 'bg-[#0A0B0E] border border-white/20 text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
        </div>

        {form.source === 'template' && (
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Template</label>
            <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="forge-deploy-template" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#FB503B] appearance-none rounded-none">
              <option value="">-- choose template --</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.type})</option>)}
            </select>
          </div>
        )}

        {form.source === 'git-inline' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Provider</label>
              <select value={form.git_provider} onChange={(e) => setForm({ ...form, git_provider: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#FB503B]">
                <option value="github">GitHub</option><option value="gitlab">GitLab</option><option value="bitbucket">Bitbucket</option><option value="custom">Custom</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Repository</label>
              <input value={form.repository} onChange={(e) => setForm({ ...form, repository: e.target.value })} data-testid="forge-deploy-repo" placeholder="acme/laravel-app" className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono focus:outline-none focus:border-[#FB503B]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Branch</label>
              <input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono focus:outline-none focus:border-[#FB503B]" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Number of Sites</label>
            <input type="number" min="1" max="50" value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) || 1 })} data-testid="forge-deploy-count" className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono text-xl focus:outline-none focus:border-[#FB503B]" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer" onClick={() => setForm({ ...form, create_database: !form.create_database })}>
            <div className={`w-5 h-5 border flex items-center justify-center ${form.create_database ? 'bg-[#FB503B] border-[#FB503B]' : 'border-white/20'}`}>{form.create_database && <CheckCircle size={16} weight="bold" className="text-white" />}</div>
            <span className="text-sm text-slate-300">Create a database per site</span>
          </label>
        </div>

        <button onClick={handleDeploy} disabled={deploying} data-testid="forge-deploy-btn" className="w-full bg-[#FB503B] text-white px-8 py-4 font-black uppercase tracking-wider hover:bg-[#e0402d] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          DEPLOY {form.count} SITE{form.count > 1 ? 'S' : ''} <ArrowRight size={20} />
        </button>
      </div>

      {deployments.length > 0 && (
        <div className="bg-card border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 bg-black/20 flex justify-between items-center">
            <h3 className="font-bold uppercase tracking-wider text-[#FB503B]">Deployment Results</h3>
            <button onClick={copyAll} className="bg-[#0A0B0E] border border-white/20 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors flex items-center gap-2"><Copy size={16} /> Copy All</button>
          </div>
          <table className="w-full text-left text-sm">
            <tbody>
              {deployments.map((d, i) => (
                <tr key={i} className="border-b border-white/10">
                  <td className="p-4 font-mono font-bold w-1/4">{d.site_name}</td>
                  <td className="p-4 w-1/4">{statusDisplay(d.status)}</td>
                  <td className="p-4 font-mono">
                    {d.url ? (
                      <div className="flex items-center gap-2">
                        <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 truncate w-64 select-all" title={d.url}>{d.url}</div>
                        <button onClick={() => { navigator.clipboard.writeText(d.url); toast.success('Copied!'); }} className="text-slate-400 hover:text-white p-1"><Copy size={16} /></button>
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#FB503B] p-1"><Eye size={16} /></a>
                      </div>
                    ) : d.status === 'failed' ? <span className="text-red-500 text-xs">{d.error}</span> : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ForgeNewDeployment;
